import { useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { Sparkles, Send, User, MessageSquare } from "lucide-react";

const SUGGESTIONS = [
  "What are my top 3 projects by sales?",
  "Compare Q2 vs Q3 collection",
  "Why might cancellations have increased?",
  "Which projects are at risk of missing FY target?",
];

export default function VexenAI() {
  const { asOf, period } = usePeriod();
  const [sessionId] = useState(() => `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/copilot", {
        session_id: sessionId,
        message: msg,
        context: { period, as_of: asOf },
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm temporarily unable to reach the intelligence layer. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header title="Vexen AI" subtitle="Conversational Decision Intelligence" />
      <div className="max-w-[1100px] mx-auto px-6 md:px-8 py-6 flex flex-col" style={{ minHeight: "calc(100vh - 56px - 64px)" }}>
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-700 flex items-center justify-center mb-5 ring-gold">
              <Sparkles size={24} className="text-white" />
            </div>
            <div className="text-2xl font-bold tracking-tight font-display mb-2">Ask anything about your portfolio.</div>
            <div className="text-sm text-slate-500 mb-8">
              Real-time intelligence across sales, collections, inventory, and market posture — answered in plain English.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-left px-4 py-3 bg-white border border-slate-200 rounded-md hover:border-slate-400 text-xs"
                  data-testid={`copilot-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-6 scroll-thin">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-700 flex-shrink-0 flex items-center justify-center">
                    <Sparkles size={13} className="text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-md text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-vexen-sidebar text-white"
                      : "bg-white border border-slate-200 text-slate-800"
                  }`}
                  data-testid={`copilot-msg-${i}`}
                >
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-slate-200 flex-shrink-0 flex items-center justify-center">
                    <User size={13} className="text-slate-600" />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-700 flex items-center justify-center">
                  <Sparkles size={13} className="text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-500">
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input */}
        <div className="sticky bottom-20 md:bottom-0 bg-[rgb(var(--bg))] pt-3 pb-2">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md p-2 shadow-sm">
            <MessageSquare size={16} className="text-slate-400 ml-2" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask Vexen AI..."
              className="flex-1 bg-transparent text-sm focus:outline-none px-2"
              data-testid="copilot-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || sending}
              className="bg-vexen-sidebar text-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
              data-testid="copilot-send"
            >
              <Send size={13} />
              Send
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
